import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import { useEffect, useState } from "react";
import { FaUserTie } from "react-icons/fa6";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { ClientInterface } from "../../common/interfaces";
import { extractErrorMessage } from "../../common/utils/axios.isAxiosError";
import {
  formatCNPJ,
  formatCPF,
  formatPhone
} from "../../common/utils/formatters";
import { truncate } from "../../common/utils/utils";
import CreateButton from "../../components/CreateButton";
import CustomTooltip from "../../components/CustomTooltip";
import {
  TypeActiveStatus,
  TypeActiveStatusLabel
} from "../../components/enum/typeActiveStatusEnum";
import HelpVideoButton from "../../components/HelpVideoButton";
import ClientCreateModal from "../../components/modal/ClientCreateModal";
import PaginationControls from "../../components/PaginationControls";
import StatusSwitch from "../../components/switch/StatusSwitch";

import PageToaster from "../../components/toaster/PageToaster";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import {
  ActionButton,
  ActionsCell,
  ClientSearchCard,
  Container,
  Table,
  TableContainer,
  TextFeldClientSearch,
  Title
} from "./client.styled";

export const ClientPage = () => {
  const [clients, setClients] = useState<ClientInterface[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<number | null>(null);
  const [clientNameToDelete, setClientNameToDelete] = useState<string | null>(
    null
  );
  const [selectedClient, setSelectedClient] = useState<ClientInterface | null>(
    null
  );
  const [search, setSearch] = useState("");

  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [loadingCompanies, setLoadingCompanies] = useState<boolean>(false);

  const [clientStatus, setClientStatus] = useState<TypeActiveStatus>(
    TypeActiveStatus.NONE
  );

  const containerId = "toaster-client-page";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const { isAdmin, isUser } = useAuth();

  const fetchClients = async (currentPage: number) => {
    let current_status;

    if (clientStatus === TypeActiveStatus.ACTIVE) {
      current_status = true;
    } else if (clientStatus === TypeActiveStatus.INACTIVE) {
      current_status = false;
    } else {
      current_status = undefined;
    }

    try {
      const response = await api.get(`/client`, {
        params: {
          page: currentPage,
          limit: 8,
          search: search.trim() !== "" ? search.trim() : undefined,
          showActives: current_status,
          company_id:
            isAdmin && selectedCompany ? selectedCompany.company_id : undefined
        }
      });

      const data = response.data.items.map((client: ClientInterface) => ({
        ...client,
        client_is_company:
          !!client.client_cnpj && client.client_cnpj.trim().length > 0
      }));

      setClients(data);
      setTotalPages(response.data.meta.totalPages);
    } catch (error) {
      const msg = extractErrorMessage(error);
      autoError(msg);
      console.error("Erro ao buscar clientes:", error);
    }
  };

  const openDeleteDialog = (client: ClientInterface) => {
    setClientToDelete(client.client_id);
    setClientNameToDelete(client.client_name);
    setDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setDialogOpen(false);
    setClientToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (clientToDelete !== null) {
      try {
        await api.delete(`/client/${clientToDelete}`);
        autoSuccess(`Cliente deletado com sucesso!`);
        fetchClients(page);
      } catch (error) {
        const msg = extractErrorMessage(error);
        autoError(msg);
      } finally {
        setDialogOpen(false);
        setClientToDelete(null);
      }
    }
  };

  const handleEdit = async (client_id: number) => {
    try {
      const response = await api.get(`/client/${client_id}`);
      setSelectedClient(response.data);
      setIsModalOpen(true);
    } catch (error) {
      const msg = extractErrorMessage(error);
      autoError(msg);

      alert("Erro ao carregar os dados do cliente.");
    }
  };

  useEffect(() => {
    fetchClients(page);
  }, [page, search, clientStatus, selectedCompany]);

  useEffect(() => {
    if (!isModalOpen) {
      fetchClients(page);
      setSearch(""); // Limpa a busca ao fechar o modal
    }
  }, [isModalOpen]);

  const handleToggleStatus = async (client: ClientInterface) => {
    try {
      let current_status = !client.client_status;
      await api.patch(`/client/${client.client_id}`, {
        client_status: !client.client_status
      });

      if (current_status) {
        autoSuccess(`Cliente ativado com sucesso!`);
      } else {
        autoWarn(`Cliente desativado com sucesso!`);
      }

      fetchClients(page); // Atualiza a lista após o toggle
    } catch (error) {
      const msg = extractErrorMessage(error);
      console.error("Erro ao alternar status do cliente:", error);
      autoError(msg);
    }
  };

  useEffect(() => {
    const url = isAdmin ? "/company/admin" : "/company";

    setLoadingCompanies(true);
    api
      .get(url)
      .then((r) => setCompanies(r.data.items || r.data))
      .catch(console.error)
      .finally(() => setLoadingCompanies(false));
  }, [isAdmin]);

  return (
    <Container>
      <PageToaster containerId={containerId} />
      <Title>
        Lista de Clientes
        <CreateButton
          onClick={() => setIsModalOpen(true)}
          style={{ marginLeft: "14px" }}
        >
          <FaUserTie size={16} /> Criar Cliente
        </CreateButton>
        <HelpVideoButton
          youtubeUrl="https://youtu.be/TgaBFKl_Qfk"
          modalTitle="Gerenciar Clientes"
          sx={{
            position: "absolute",
            top: 0,
            right: 8,
            zIndex: 2
          }}
        />
      </Title>

      <ClientSearchCard>
        <TextFeldClientSearch
          id="user_search"
          label="Dados do cliente"
          variant="outlined"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1); // reseta para primeira página ao buscar
          }}
        />

        <FormControl>
          <InputLabel id="label-status">Status</InputLabel>
          <Select
            sx={{ width: "200px" }}
            labelId="label-status"
            value={clientStatus}
            label="Status"
            onChange={(e) =>
              setClientStatus(e.target.value as TypeActiveStatus)
            }
          >
            {(Object.values(TypeActiveStatus) as TypeActiveStatus[])
              // .filter(
              //     (code) => code !== TypeActiveStatus.NONE
              // )
              .map((statusCode) => (
                <MenuItem key={statusCode} value={statusCode}>
                  {TypeActiveStatusLabel[statusCode]}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        {isAdmin && (
          <Autocomplete
            sx={{ width: 400 }}
            options={companies}
            getOptionLabel={(opt) => opt.company_name || ""}
            value={selectedCompany}
            isOptionEqualToValue={(option, value) =>
              option?.company_id === value?.company_id
            }
            onChange={(_, v) => setSelectedCompany(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Empresa"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCompanies && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        )}
      </ClientSearchCard>

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Nome</th>
              {isAdmin && <th>Empresa</th>}
              <th>Documento</th>
              {/* <th>Tipo</th> */}
              <th>Telefone</th>
              {isAdmin && <th>Criado em</th>}
              {!isUser && <th>Ações</th>}
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => (
              <tr key={client.client_id}>
                <td>{client.client_name}</td>
                {isAdmin && (
                  <td>{truncate(client.company.company_name, 15)}</td>
                )}
                <td style={{ whiteSpace: "nowrap" }}>
                  {client.client_is_company
                    ? formatCNPJ(client.client_cnpj)
                    : formatCPF(client.client_cpf)}
                </td>
                {/* <td>
                  {client.client_is_company ? "Empresa" : "Pessoa Física"}
                </td> */}
                <td style={{ whiteSpace: "nowrap" }}>
                  {formatPhone(client.phone?.phone_number) || "N/A"}
                </td>
                {isAdmin && (
                  <td>
                    {new Date(client.created_at).toLocaleDateString("pt-BR")}
                  </td>
                )}

                {!isUser && (
                  <ActionsCell>
                    <CustomTooltip
                      title={
                        client.client_status
                          ? "Desativar Cliente"
                          : "Ativar Cliente"
                      }
                    >
                      <StatusSwitch
                        checked={client.client_status}
                        onToggle={() => handleToggleStatus(client)}
                      />
                    </CustomTooltip>

                    <CustomTooltip title="Editar Cliente">
                      <ActionButton
                        onClick={() => handleEdit(client.client_id)}
                      >
                        <FiEdit size={20} color="#4caf50" />
                      </ActionButton>
                    </CustomTooltip>

                    <CustomTooltip title="Excluir Cliente">
                      <ActionButton onClick={() => openDeleteDialog(client)}>
                        <FiTrash2 size={20} color="#f44336" />
                      </ActionButton>
                    </CustomTooltip>
                    <FiTrash2 size={35} color="#f4433600" />
                  </ActionsCell>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>

      <PaginationControls
        page={page}
        totalPages={totalPages}
        setPage={setPage}
      />

      <Dialog open={dialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir {clientNameToDelete}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="secondary">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="primary"
            variant="contained"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <ClientCreateModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedClient(null);
        }}
        client={selectedClient}
      />
    </Container>
  );
};
